import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import Layout from '../../../app/Layout';
import HeaderContent from '../General/HeaderContent';
import Breadcrumb from '../General/Breadcrumb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMailBulk } from '@fortawesome/free-solid-svg-icons';
import SpinnerLoading from '../../components/Spinners/SpinnerLoading';
import ModalBasicwithRedux from '../../../containers/ModalBasicwithRedux';
import ModalLargewhithRedux from '../../../containers/ModalLargewhithRedux';
import Swal from 'sweetalert2';
import {
  guardarGestion,
  listarEstados,
  listarObservaciones,
  registrarClick,
  traerDetallesLead,
} from '../../../actions/leadsActions';
import { listarProyectos } from '../../../actions/cursosActions';
import { updateStateModalLarge } from '../../../actions/modalActions';
import FormularioDatosCorreo from './FormularioDatosCorreo';
import './Trabajadores.scss';

const DetallesLead = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id_lead } = useParams();
  const { usuario } = useSelector(state => state.usuario);
  const { proyectos } = useSelector(state => state.cursos);
  const { estados, observaciones, leads } = useSelector(state => state.leads);

  const [lead, setLead] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentLeadId, setCurrentLeadId] = useState(id_lead);

  // CA09: call panel state
  const [timerActive, setTimerActive] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [inicioConversacion, setInicioConversacion] = useState(0);
  const [resultado, setResultado] = useState(null); // 'respondio' | 'no_respondio'
  const timerRef = useRef(null);

  // CA11, CA12, CA13: gestión form
  const [estado, setEstado] = useState('');
  const [proposito, setProposito] = useState('');
  const [nota, setNota] = useState('');

  // CA14: programar seguimiento
  const [fechaProg, setFechaProg] = useState('');
  const [horaProg, setHoraProg] = useState('');
  const [motivoProg, setMotivoProg] = useState('');

  // CA17: change tracking
  const [hasChanges, setHasChanges] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // currentLeadIndex derived from leads array (always stays in sync after CA05 removal)
  const currentLeadIndex = leads.findIndex(l => String(l.id_lead) === String(currentLeadId));

  // timer effect
  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => setSeconds(prev => prev + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  const resetCallState = () => {
    setTimerActive(false);
    clearInterval(timerRef.current);
    setSeconds(0);
    setInicioConversacion(0);
    setResultado(null);
  };

  const resetGestionState = () => {
    setProposito('');
    setNota('');
    setFechaProg('');
    setHoraProg('');
    setMotivoProg('');
    setHasChanges(false);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m < 10 ? '0' + m : m}:${sec < 10 ? '0' + sec : sec}`;
  };

  const formatTiempo = (secs) => {
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.ceil(secs % 60);
    return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  };

  const fetchLeadDetails = async (leadId) => {
    setLoading(true);
    const datosLead = await dispatch(traerDetallesLead(leadId));
    if (datosLead) {
      const l = datosLead[0];
      setLead(l);
      setEstado(l.estado || '');
      setNota(l.notas || '');
      setLoading(false);
    }
  };

  // CA08: reset all state when lead changes
  useEffect(() => {
    dispatch(listarEstados());
    dispatch(listarObservaciones());
    dispatch(listarProyectos());
    fetchLeadDetails(currentLeadId);
    resetCallState();
    resetGestionState();
  }, [currentLeadId]);

  const handleLeadChange = (evt) => {
    setLead({ ...lead, [evt.target.name]: evt.target.value });
    setHasChanges(true);
  };

  // CA15: single save action
  const handleGuardar = async (e) => {
    e.preventDefault();
    // RN01: los 4 campos son obligatorios para considerar un lead gestionado
    if (!resultado) {
      Swal.fire({ icon: 'warning', title: 'Resultado requerido', text: 'Marca si el lead Respondió o No respondió.' });
      return;
    }
    if (!proposito) {
      Swal.fire({ icon: 'warning', title: 'Propósito requerido', text: 'Selecciona un propósito antes de guardar.' });
      return;
    }
    if (!estado) {
      Swal.fire({ icon: 'warning', title: 'Estado requerido', text: 'Selecciona el estado del lead.' });
      return;
    }
    if (!nota || nota.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Nota requerida', text: 'Agrega una nota de gestión antes de guardar.' });
      return;
    }

    setGuardando(true);
    const tiempoConversado = inicioConversacion > 0 ? seconds - inicioConversacion : 0;

    const datosGestion = {
      id_lead: lead.id_lead,
      id_usuario: usuario.id_usuario,
      proposito,
      nota,
      estado,
      segundos: seconds,
      tiempoConversado: tiempoConversado > 0 ? tiempoConversado : 0,
      respondio: resultado === 'respondio',
      fechaProg,
      horaProg,
      motivoProg,
      nombres: lead.nombres,
      apellidos: lead.apellidos,
      correo: lead.correo,
      producto: lead.producto,
      observacion_l: lead.observacion_l,
      telefono: lead.telefono,
    };

    // Use old leads snapshot before Redux removes the lead
    const oldLeads = leads;
    const oldIndex = oldLeads.findIndex(l => String(l.id_lead) === String(currentLeadId));

    const rpta = await dispatch(guardarGestion(datosGestion, lead));
    setGuardando(false);

    if (rpta && rpta.exito) {
      // CA16: success message
      await Swal.fire({
        icon: 'success',
        title: 'Gestión guardada',
        text: 'Lead descontado de la cola pendiente.',
        timer: 1800,
        showConfirmButton: false,
      });
      resetCallState();
      resetGestionState();

      // CA07: navigate to next pending lead
      const nextLead = oldLeads[oldIndex + 1];
      const prevLead = oldLeads[oldIndex - 1];
      if (nextLead) {
        setCurrentLeadId(nextLead.id_lead);
      } else if (prevLead) {
        setCurrentLeadId(prevLead.id_lead);
      } else {
        history.push('/leads');
      }
    }
  };

  // CA17: warn before Siguiente if unsaved
  const handleSiguiente = () => {
    if (hasChanges) {
      Swal.fire({
        icon: 'warning',
        title: '¿Continuar sin guardar?',
        text: 'Tienes cambios sin guardar en este lead.',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
      }).then(r => { if (r.isConfirmed) doSiguiente(); });
      return;
    }
    doSiguiente();
  };

  const doSiguiente = () => {
    if (currentLeadIndex === -1 || leads.length === 0) { history.push('/leads'); return; }
    if (currentLeadIndex < leads.length - 1) {
      setCurrentLeadId(leads[currentLeadIndex + 1].id_lead);
    } else {
      Swal.fire({ icon: 'info', title: 'No hay más leads', timer: 1500, showConfirmButton: false });
    }
  };

  const handleEnviarCorreo = () => {
    dispatch(updateStateModalLarge(<FormularioDatosCorreo registro={lead} />, 'Enviar correo'));
  };

  const handleRegistrarClick = (tipo) => {
    dispatch(registrarClick({ autor: usuario.id_usuario, id_lead: lead.id_lead, tipo }));
  };

  return (
    <Layout>
      <div className="Trabajadores">
        <div className="Trabajadores__container">
          <div className="Trabajadores__head">
            <HeaderContent title="Detalle de Lead" icon="group" />
            <Breadcrumb link={[
              { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
              { url: '/leads', nombre: 'Leads' },
              { url: '', nombre: 'Detalle' },
            ]} />
          </div>
          <div className="Trabajadores__body container-fluid px-0 content-actions mt-1">
            <ModalBasicwithRedux />
            <ModalLargewhithRedux />
            <SpinnerLoading loading={loading}>
              <form onSubmit={handleGuardar}>
                <div className="row">

                  {/* ── LEFT PANEL ── */}
                  <div className="col-md-5">

                    {/* CA19: Datos del Lead */}
                    <div className="box mt-2 p-3">
                      <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#434343' }}>
                        <i className="fas fa-user mr-2"></i>Datos del Lead
                      </h5>
                      <div className="form-group mb-2">
                        <label className="mb-1">Nombre completo:</label>
                        <input type="text" className="form-control form-control-sm"
                          value={`${lead.nombres || ''} ${lead.apellidos || ''}`} readOnly />
                      </div>
                      <div className="form-group mb-2">
                        <label className="mb-1">Teléfono:</label>
                        <div className="input-group input-group-sm">
                          <input type="text" className="form-control" value={lead.telefono || ''} readOnly />
                          <div className="input-group-append">
                            <a href={`tel:+${lead.telefono}`} className="btn btn-warning" title="Llamar por teléfono">
                              <i className="fas fa-mobile-alt"></i>
                            </a>
                            <a href={`https://api.whatsapp.com/send/?phone=${lead.telefono}`}
                              className="btn btn-success" target="_blank" rel="noopener noreferrer"
                              title="WhatsApp" onClick={() => handleRegistrarClick(1)}>
                              <i className="fab fa-whatsapp"></i>
                            </a>
                            <button type="button" className="btn btn-info" onClick={handleEnviarCorreo} title="Enviar correo">
                              <FontAwesomeIcon icon={faMailBulk} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="form-group mb-2">
                        <label className="mb-1">Correo:</label>
                        <input type="email" className="form-control form-control-sm"
                          name="correo" value={lead.correo || ''} onChange={handleLeadChange} />
                      </div>
                      <div className="form-group mb-2">
                        <label className="mb-1">Producto:</label>
                        <select name="producto" className="form-control form-control-sm"
                          value={lead.producto || ''} onChange={handleLeadChange}>
                          {proyectos.map((p, i) => (
                            <option key={i} value={p.id_proyecto}>{p.nombre_proyecto}</option>
                          ))}
                        </select>
                      </div>
                      <small className="text-muted">
                        Tiempo acumulado: <strong>{formatTiempo(lead.tiempo_llamada || 0)}</strong>
                      </small>
                    </div>

                    {/* CA09, CA10: Llamada Actual */}
                    <div className="box mt-2 p-3">
                      <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#434343' }}>
                        <i className="fas fa-phone mr-2"></i>Llamada Actual
                      </h5>
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                        <button type="button"
                          className={`btn btn-sm ${timerActive ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => { setTimerActive(v => !v); setHasChanges(true); }}>
                          <i className={`fas fa-${timerActive ? 'phone-slash' : 'phone'} mr-1`}></i>
                          {timerActive ? 'Colgar' : 'Llamar'}
                        </button>
                        <button type="button" className="btn btn-sm btn-secondary"
                          onClick={resetCallState}>
                          <i className="fas fa-undo mr-1"></i>Reiniciar
                        </button>
                        <span className="font-weight-bold ml-1">
                          <i className="fas fa-stopwatch mr-1"></i>{formatTime(seconds)}
                        </span>
                      </div>
                      {/* CA10: resultado rápido */}
                      <div className="d-flex mt-3" style={{ gap: '8px' }}>
                        <button type="button"
                          className={`btn btn-sm ${resultado === 'respondio' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => {
                            setResultado('respondio');
                            setInicioConversacion(seconds);
                            setHasChanges(true);
                          }}>
                          <i className="fas fa-check mr-1"></i>Respondió
                        </button>
                        <button type="button"
                          className={`btn btn-sm ${resultado === 'no_respondio' ? 'btn-warning' : 'btn-outline-warning'}`}
                          onClick={() => {
                            setResultado('no_respondio');
                            setProposito('4'); // RN03: auto-aplica "No contactado"
                            setHasChanges(true);
                          }}>
                          <i className="fas fa-times mr-1"></i>No respondió
                        </button>
                      </div>
                      {resultado && (
                        <div className="mt-2">
                          <span className={`badge badge-${resultado === 'respondio' ? 'primary' : 'warning'}`}>
                            {resultado === 'respondio' ? '✓ Respondió' : '✗ No respondió'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── RIGHT PANEL ── */}
                  <div className="col-md-7">

                    {/* CA11–CA13, CA15: Gestión Comercial */}
                    <div className="box mt-2 p-3">
                      <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#434343' }}>
                        <i className="fas fa-clipboard-list mr-2"></i>Gestión Comercial
                      </h5>
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label className="mb-1">Estado:</label>
                          <select className="form-control form-control-sm"
                            value={estado}
                            onChange={e => { setEstado(e.target.value); setHasChanges(true); }}>
                            <option value="">Seleccionar...</option>
                            {estados.map((e, i) => (
                              <option key={i} value={e.id_estado}>{e.nombre_estado}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-2">
                          {/* CA11: propósito obligatorio */}
                          <label className="mb-1">
                            Propósito: <span className="text-danger">*</span>
                          </label>
                          <select className="form-control form-control-sm"
                            value={proposito}
                            onChange={e => { setProposito(e.target.value); setHasChanges(true); }}>
                            <option value="">Seleccionar...</option>
                            <option value="2">Venta</option>
                            <option value="3">Seguimiento de venta</option>
                            <option value="1">Posventa</option>
                            <option value="4">No contactado</option>
                          </select>
                        </div>
                        {String(estado) === '5' && (
                          <div className="col-12 mb-2">
                            <label className="mb-1">Observaciones:</label>
                            <select name="observacion_l" className="form-control form-control-sm"
                              value={lead.observacion_l || ''} onChange={handleLeadChange}>
                              {observaciones.map((o, i) => (
                                <option key={i} value={o.id_observacion}>{o.observacion_n}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {/* CA12, CA13: único campo de nota */}
                        <div className="col-12 mb-2">
                          <label className="mb-1">Nota de gestión:</label>
                          <textarea className="form-control form-control-sm" rows={3}
                            value={nota}
                            onChange={e => { setNota(e.target.value); setHasChanges(true); }}
                            placeholder="Anota el resultado de esta gestión..." />
                        </div>
                      </div>
                    </div>

                    {/* CA14: Programar Seguimiento (inline) */}
                    <div className="box mt-2 p-3" style={resultado === 'no_respondio' ? { border: '2px solid #ffc107', borderRadius: '4px' } : {}}>
                      <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#434343' }}>
                        <i className="fas fa-calendar-alt mr-2"></i>Programar Seguimiento
                        {resultado === 'no_respondio'
                          ? <small className="text-warning ml-2" style={{ fontSize: '12px' }}><i className="fas fa-exclamation-circle mr-1"></i>Agenda la próxima llamada</small>
                          : <small className="text-muted ml-2" style={{ fontSize: '12px' }}>(opcional)</small>
                        }
                      </h5>
                      <div className="row">
                        <div className="col-md-4 mb-2">
                          <label className="mb-1">Fecha:</label>
                          <input type="date" className="form-control form-control-sm"
                            value={fechaProg}
                            onChange={e => { setFechaProg(e.target.value); setHasChanges(true); }} />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label className="mb-1">Hora:</label>
                          <input type="time" className="form-control form-control-sm"
                            value={horaProg}
                            onChange={e => { setHoraProg(e.target.value); setHasChanges(true); }} />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label className="mb-1">Motivo:</label>
                          <input type="text" className="form-control form-control-sm"
                            value={motivoProg} placeholder="Motivo..."
                            onChange={e => { setMotivoProg(e.target.value); setHasChanges(true); }} />
                        </div>
                      </div>
                    </div>

                    {/* CA18: Historial (solo del lead actual, desde API) */}
                    <div className="box mt-2 p-3">
                      <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', color: '#434343' }}>
                        <i className="fas fa-history mr-2"></i>Historial de Gestiones
                      </h5>
                      {lead.llamadas && lead.llamadas.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm table-striped mb-0">
                            <thead>
                              <tr>
                                <th>Fecha</th>
                                <th>Propósito</th>
                                <th>Duración</th>
                                <th>Nota</th>
                                <th>Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lead.llamadas.map((ll, i) => (
                                <tr key={i}>
                                  <td style={{ whiteSpace: 'nowrap' }}>{ll.fecha_hora || '—'}</td>
                                  <td>{ll.nombre_proposito || '—'}</td>
                                  <td style={{ whiteSpace: 'nowrap' }}>{formatTiempo(ll.tiempo_llamada || 0)}</td>
                                  <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={ll.anotaciones}>{ll.anotaciones || '—'}</td>
                                  <td>
                                    <span className={`badge badge-${ll.completado === '1' ? 'success' : 'warning'}`}>
                                      {ll.completado === '1' ? 'Completado' : 'Pendiente'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No hay gestiones registradas.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* CA15: barra de acciones única */}
                <div className="row mt-3 mb-3">
                  <div className="col-12 d-flex justify-content-between align-items-center">
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={() => history.push('/leads')}>
                      <i className="fas fa-arrow-left mr-1"></i>Volver a lista
                    </button>
                    <div>
                      <button type="submit" className="btn btn-primary mr-2" disabled={guardando}>
                        <i className="fas fa-save mr-1"></i>
                        {guardando ? 'Guardando...' : 'Guardar gestión'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={handleSiguiente}>
                        Siguiente lead <i className="fas fa-arrow-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </SpinnerLoading>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DetallesLead;
